import Layout from "@/components/Layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, ShieldCheck, Truck, Zap, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
const Index = () => {
  const {
    user
  } = useAuth();
  const {
    data: featuredProducts,
    isLoading
  } = useQuery({
    queryKey: ["featured-products"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("products").select("*").eq("is_featured", true).limit(6);
      if (error) throw error;
      return data;
    }
  });
  const addToCart = async (productId: string) => {
    if (!user) {
      toast.error("Morate biti prijavljeni da biste dodali proizvod u korpu");
      return;
    }
    try {
      const {
        error
      } = await supabase.from("cart_items").upsert({
        user_id: user.id,
        product_id: productId,
        quantity: 1
      }, {
        onConflict: "user_id,product_id"
      });
      if (error) throw error;
      toast.success("Proizvod dodat u korpu!");
    } catch (error: any) {
      toast.error("Greška pri dodavanju u korpu");
    }
  };
  const features = [{
    icon: ShieldCheck,
    title: "Garancija Kvaliteta",
    description: "Samo provjereni brendovi i certifikovani proizvodi"
  }, {
    icon: Truck,
    title: "Brza Dostava",
    description: "Besplatna dostava širom Crne Gore za porudžbine preko 50€"
  }, {
    icon: Zap,
    title: "Stručna Pomoć",
    description: "Naš tim stručnjaka je tu da vam pomogne u izboru"
  }];
  return <Layout>
      {/* Hero Section */}
      <section className="gradient-hero text-white py-20 md:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Vaš pouzdan partner za elektro materijal</h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90">Kvalitet, pouzdanost i brza dostava širom Crne Gore. Više od 15 godina tradicije.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-accent hover:bg-accent-hover text-white">
                <Link to="/proizvodi">
                  Pregledaj Proizvode <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 text-white border-white hover:bg-white hover:text-primary">
                <Link to="/kontakt">Kontaktirajte Nas</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <h2 className="sr-only">Naše Prednosti</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => <Card key={index} className="border-0 shadow-card hover:shadow-card-hover transition-smooth">
                <CardHeader>
                  <div className="w-12 h-12 rounded-lg gradient-primary flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle>{feature.title}</CardTitle>
                  <CardDescription className="text-base">{feature.description}</CardDescription>
                </CardHeader>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Izdvojeni Proizvodi</h2>
            <p className="text-muted-foreground text-lg">Najpopularniji proizvodi iz naše ponude</p>
          </div>
          
          {isLoading ? <div className="text-center py-12">Učitavanje proizvoda...</div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {featuredProducts?.map(product => <Card key={product.id} className="shadow-card hover:shadow-card-hover transition-smooth">
                  <CardHeader>
                    <div className="aspect-square bg-muted rounded-lg mb-4 flex items-center justify-center">
                      {product.image_url ? <img src={product.image_url} alt={product.name} className="w-full h-full object-cover rounded-lg" /> : <div className="text-6xl text-muted-foreground">⚡</div>}
                    </div>
                    <CardTitle className="line-clamp-1">{product.name}</CardTitle>
                    <CardDescription className="line-clamp-2">{product.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-2xl font-bold text-primary">{product.price}€</div>
                        <div className="text-sm text-muted-foreground">
                          Na stanju: {product.stock}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button onClick={() => addToCart(product.id)} className="w-full gradient-accent text-white" disabled={product.stock === 0}>
                      <Plus className="h-4 w-4 mr-2" />
                      {product.stock === 0 ? "Nema na stanju" : "Dodaj u Korpu"}
                    </Button>
                  </CardFooter>
                </Card>)}
            </div>}
          
          <div className="text-center">
            <Button asChild size="lg" className="gradient-accent text-white">
              <Link to="/proizvodi">
                Pogledaj Sve Proizvode <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>;
};
export default Index;